import { Button, DatePicker, Input, Select } from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CHANGE_AUDIT_ACTIONS,
  changeAudit,
  type ChangeAuditEntry,
  type ChangeAuditPage,
  type ChangeAuditSource,
} from "@/api/changeAudit.service";
import { DataTable } from "@/components/DataTable/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { useQuery } from "@/hooks/useApi";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { ActorPicker } from "./ActorPicker";
import { AuditEntryDetail } from "./AuditEntryDetail";
import { actionLabel } from "./auditCells";
import styles from "./AuditLog.module.css";
import { createAuditLogColumns } from "./columns";
import { RESEARCH_TABLES } from "./researchLabels";

const PAGE_SIZE = 50;

const TABS: { id: ChangeAuditSource; label: string }[] = [
  { id: "approval-requests", label: "Approval requests" },
  { id: "testimonials", label: "Testimonials" },
  { id: "research-cms", label: "Research CMS" },
];

const TABLE_OPTIONS = [{ value: "", label: "All tables" }, ...RESEARCH_TABLES];

/** Turn a yyyy-MM-dd filter into a bound covering the whole of that day. */
function dateToIso(date: string | undefined, edge: "start" | "end"): string | undefined {
  if (!date) return undefined;
  const suffix = edge === "start" ? "T00:00:00.000Z" : "T23:59:59.999Z";
  const parsed = new Date(`${date}${suffix}`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function isSource(value: string | null): value is ChangeAuditSource {
  return TABS.some(tab => tab.id === value);
}

function AuditTabs({
  active,
  onChange,
}: {
  active: ChangeAuditSource;
  onChange: (source: ChangeAuditSource) => void;
}) {
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (e.key === "ArrowRight") nextIndex = (index + 1) % TABS.length;
    else if (e.key === "ArrowLeft") nextIndex = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = TABS.length - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    const next = TABS[nextIndex];
    onChange(next.id);
    document.getElementById(`audit-tab-${next.id}`)?.focus();
  };

  return (
    <div className={styles.tabs} role="tablist" aria-label="Audit logs">
      {TABS.map((tab, index) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`audit-tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls="audit-panel"
            tabIndex={isActive ? 0 : -1}
            className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
            onClick={() => onChange(tab.id)}
            onKeyDown={e => handleKeyDown(e, index)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<ChangeAuditEntry | null>(null);

  const logParam = searchParams.get("log");
  const source: ChangeAuditSource = isSource(logParam) ? logParam : "approval-requests";
  const actor = searchParams.get("actor") ?? "";
  const actionParam = searchParams.get("action") ?? "";
  const action = CHANGE_AUDIT_ACTIONS[source].includes(actionParam) ? actionParam : "";
  const firm = searchParams.get("firm") ?? "";
  const tableParam = searchParams.get("table") ?? "";
  const table = RESEARCH_TABLES.some(option => option.value === tableParam) ? tableParam : "";
  const isResearch = source === "research-cms";
  const entityId = searchParams.get("entityId") ?? "";
  const fromDate = searchParams.get("from") ?? "";
  const toDate = searchParams.get("to") ?? "";

  // The text filters are substring searches, so wait for typing to stop before asking.
  const debouncedFirm = useDebouncedValue(firm);
  const debouncedEntityId = useDebouncedValue(entityId);

  const filters = useMemo(
    () => ({
      actor: actor || undefined,
      action: action || undefined,
      firm: isResearch ? undefined : debouncedFirm || undefined,
      table: isResearch ? table || undefined : undefined,
      entityId: debouncedEntityId || undefined,
      from: dateToIso(fromDate || undefined, "start"),
      to: dateToIso(toDate || undefined, "end"),
      limit: PAGE_SIZE,
    }),
    [actor, action, isResearch, debouncedFirm, table, debouncedEntityId, fromDate, toDate],
  );

  const { data, loading, error } = useQuery(
    () => changeAudit.list(source, filters),
    [source, filters],
  );

  // Later pages are appended to the first one; a new first page (filters or tab changed) drops them.
  const [more, setMore] = useState<ChangeAuditPage | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<Error | null>(null);
  const firstPage = useRef(data);
  firstPage.current = data;
  useEffect(() => {
    setMore(null);
    setMoreError(null);
  }, [data]);

  const actionOptions = useMemo(
    () => [
      { value: "", label: "All actions" },
      ...CHANGE_AUDIT_ACTIONS[source].map(value => ({ value, label: actionLabel(source, value) })),
    ],
    [source],
  );

  function setParam(key: string, value: string | null) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }

  function selectTab(next: ChangeAuditSource) {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set("log", next);
      // Each log has its own actions and ids; the people, firm and dates carry over.
      params.delete("action");
      params.delete("entityId");
      params.delete("table");
      return params;
    });
  }

  const rows = [...(data?.items ?? []), ...(more?.items ?? [])];
  const nextCursor = more ? more.nextCursor : (data?.nextCursor ?? null);

  async function loadMore() {
    if (!nextCursor) return;
    const requestedFor = firstPage.current;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await changeAudit.list(source, { ...filters, cursor: nextCursor });
      // The filters changed while this page was on its way, so it no longer belongs to the list.
      if (firstPage.current !== requestedFor) return;
      setMore(previous => ({
        items: [...(previous?.items ?? []), ...page.items],
        nextCursor: page.nextCursor,
      }));
    } catch (err) {
      if (firstPage.current === requestedFor) {
        setMoreError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      setLoadingMore(false);
    }
  }

  const columns = useMemo(() => createAuditLogColumns({ source, onSelect: setSelected }), [source]);

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Audit log"
        subtitle="Every change made to approval requests, testimonials and the research CMS, newest first."
      />

      <AuditTabs active={source} onChange={selectTab} />

      <div
        id="audit-panel"
        role="tabpanel"
        aria-labelledby={`audit-tab-${source}`}
        className={styles.tabPanel}
      >
        <div className={styles.filters}>
          <div className={styles.filterCell}>
            <ActorPicker value={actor} onChange={next => setParam("actor", next)} />
          </div>
          <div className={styles.filterCell}>
            <Select
              label="Action"
              value={action}
              options={actionOptions}
              onChange={e => setParam("action", e.target.value || null)}
              size="small"
            />
          </div>
          <div className={styles.filterCell}>
            {isResearch ? (
              <Select
                label="Table"
                value={table}
                options={TABLE_OPTIONS}
                onChange={e => setParam("table", e.target.value || null)}
                size="small"
              />
            ) : (
              <Input
                label="Firm"
                placeholder="Firm name"
                value={firm}
                onChange={e => setParam("firm", e.target.value || null)}
              />
            )}
          </div>
          <div className={styles.filterCell}>
            <Input
              label="Entity id"
              placeholder="Full id"
              value={entityId}
              onChange={e => setParam("entityId", e.target.value || null)}
            />
          </div>
          <div className={styles.filterCell}>
            <DatePicker
              label="From"
              value={fromDate}
              onChange={e => setParam("from", e.target.value || null)}
            />
          </div>
          <div className={styles.filterCell}>
            <DatePicker
              label="To"
              value={toDate}
              onChange={e => setParam("to", e.target.value || null)}
            />
          </div>
        </div>

        <div className={styles.contentSection}>
          {loading ? (
            <p role="status" className={styles.muted}>
              Loading the audit log…
            </p>
          ) : error ? (
            <p role="alert" className={styles.errorBanner}>
              {error.message}
            </p>
          ) : rows.length === 0 ? (
            <p className={styles.emptyState}>No changes match these filters.</p>
          ) : (
            <>
              <DataTable data={rows} columns={columns} rowKey="id" onRowClick={setSelected} />
              {moreError && (
                <p role="alert" className={styles.errorBanner}>
                  {moreError.message}
                </p>
              )}
              {nextCursor && (
                <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load more"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <AuditEntryDetail entry={selected} source={source} onClose={() => setSelected(null)} />
    </div>
  );
}

export default AuditLogPage;
